import { ResponseDataMain, ResponseDataSummary, ResponseReportFactory } from "../types/response";
import { getQuarterNumber, filterByRange, findQuestionnaire, isEmptyString } from "./utils";
import { Quarter } from "../types/data";

const hasFactory = (item, factory) => item.factories?.some((f) => f.title === factory);

export const buildDataMain = (response: ResponseDataMain, quarter: Quarter, year: number) => {
  const departments = response.departments.sort((a, b) => {
    if (a.order === null && b.order === null) return 0;
    if (a.order === null) return 1;
    if (b.order === null) return -1;
    return a.order - b.order;
  });

  const blocks = response.blocks;
  const statusQuestionnaire = response.statusQuestionnaire.length > 0
  ? {
      id: response.statusQuestionnaire?.[0].id,
      title: response.statusQuestionnaire?.[0].statusQuestionnaire
    }
  : {
      id: null,
      title: 'Не начато'
    }
  ;
  const actions = response.actions;
  const comments = response.comments;

  // Создаем индексы для быстрого поиска ответов
  const answerMap = new Map<string, { answer: string; isEdit?: boolean }>();

  for (const answer of response.answers) {
    const key = `${answer.requirement.id}_${answer.department.id}`;

    answerMap.set(key, {
      answerId: answer.id ?? null,
      answer: answer.answer,
      isEdit: answer.isEdit ?? false,
    });
  }

  const filteredBlocks = filterByRange(blocks, quarter, year);

  const data = filteredBlocks.map(block => {
    const filteredRequirements = filterByRange(block.requirements, quarter, year);

    const groupRequirements = filteredRequirements.map(requirement => {
      const requirementAnswers = departments.map(department => {
        const key = `${requirement.id}_${department.id}`;
        const answerObj = answerMap.get(key);

        return {
          answerId: answerObj?.answerId ?? null,
          departmentId: department.id,
          department: department.title,
          answer: answerObj?.answer || 'Нет',
          isEdit: answerObj?.isEdit ?? false,
        };
      });

      const requirementActions = actions
        .filter((action) => action.demandID === requirement.id)
        .map((action) => String(action.id));

      const isComments = comments.some((comment) => comment.demandID === requirement.id);

      return {
        requirementId: requirement.id,
        title: requirement.Title,
        answers: requirementAnswers,
        isFocal: requirement.isFocal,
        documentation: isEmptyString(requirement.documentation.text) ? null : requirement.documentation,
        actions: requirementActions.length > 0 ? requirementActions : null,
        comments: isComments ? ['Comment'] : null
      };
    });

    return {
      id: block.id,
      title: block.Title,
      groupRequirements
    };
  });

  return {
    data,
    dataHead: [...departments],
    statusQuestionnaire: statusQuestionnaire
  };
}

export const buildDataSummary = (response: ResponseDataSummary, currentQuarter: Quarter, currentYear: number) => {
  const quarters: Quarter[] = ['Q1', 'Q2', 'Q3', 'Q4'];
  const currentDate = getQuarterNumber(currentYear, currentQuarter);

  // Создаем индексы для быстрого поиска ответов
  const answerMap = new Map();

  for (const answer of response.answers) {
    const key = [
      answer.year,
      answer.quarter,
      answer.department?.id,
      answer.requirement?.id
    ].join('|');

    answerMap.set(key, answer.answer);
  }

  return filterByRange(response.directions, currentQuarter, currentYear).map((direction) => {
    return {
      title: direction.title,
      quarters: quarters.map((quarter) => {
        const currentQuarterYear = getQuarterNumber(currentYear, quarter);
        if (currentQuarterYear > currentDate) {
          return { quarter: quarter, blockRequirements: null };
        }

        const blocks = filterByRange(direction.blocks || [], quarter, currentYear).map((block) => {
          const groupRequirements = filterByRange(block.requirements || [], quarter, currentYear).map((requirement) => {
            const answersList = (direction.departments || []).map((department) => {
              const key = [currentYear, quarter, department.id, requirement.id].join('|');
              const answer = answerMap.get(key);

              return {
                department: department.title,
                answer: answer || 'Нет',
              };
            });

            return {
              title: requirement.title,
              answers: answersList,
              isFocal: requirement.isFocal
            };
          });

          return {
            title: block.title,
            groupRequirements
          };
        });

        return {
          quarter: quarter,
          blockRequirements: blocks.length > 0 ? blocks : null
        };
      })
    };
  });
}

export const buildReportFactory = (response: ResponseReportFactory) => {
  // Создаем индексы для быстрого поиска ответов
  const answerMap = new Map();

  for (const answer of response.data.answers) {
    const key = [
      answer.year,
      answer.quarter,
      answer.factory.title,
      answer.department.id,
      answer.requirement.id,
    ].join('|');

    answerMap.set(key, answer.answer);
  }

  const result = [];

  for (const year of response.params.year) {
    for (const quarter of response.params.quarter) {
      for (const factory of response.data.factories) {
        for (const gop of filterByRange(response.data.gops, quarter, year)) {
          const filteredDirections = filterByRange(gop.directions, quarter, year);

          const currentDirections =
            response.params.filters.direction.selected.length > 0
              ? filteredDirections.filter((item) =>
                  response.params.filters.direction.selected.includes(item.title),
                )
              : filteredDirections;

          for (const direction of currentDirections) {
            const blockRequirements = [];

            const filteredBlocks = filterByRange(direction.blocks, quarter, year);

            const currentBlocks =
              response.params.filters.blockDemand.selected.length > 0
                ? filteredBlocks.filter((item) =>
                    response.params.filters.blockDemand.selected.includes(item.title),
                  )
                : filteredBlocks;

            for (const block of currentBlocks) {
              const groupRequirements = [];

              const filteredRequirements = filterByRange(block.requirements, quarter, year);

              for (const requirement of filteredRequirements) {
                const answersByDepartment = [];

                const filteredDepartments =
                  response.params.filters.department.selected.length > 0
                    ? direction.departments.filter((item) =>
                        response.params.filters.department.selected.includes(item.title),
                      )
                    : direction.departments.filter((item) =>
                        response.data.departments.some((dep) => dep.title === item.title),
                      );

                const sortDepartments = filteredDepartments.sort((a, b) => {
                  if (a.order === null && b.order === null) return 0;
                  if (a.order === null) return 1;
                  if (b.order === null) return -1;
                  return a.order - b.order;
                });

                // Добавляем отделам списки заводов
                const currentDepartments = sortDepartments.map((dep) => {
                  const match = response.data.departments.find(item => item.title === dep.title);

                  return {
                      ...dep,
                      factories: match ? match.factories : []
                  };
                });

                for (const department of currentDepartments) {
                  if (!hasFactory(department, factory.title)) {
                    continue;
                  }

                  const key = [
                    year,
                    quarter,
                    factory.title,
                    department.id,
                    requirement.id,
                  ].join('|');

                  const answer = answerMap.get(key) || 'Нет';

                  answersByDepartment.push({
                    department: department.title,
                    answer,
                  });
                }

                groupRequirements.push({
                  title: requirement.title,
                  answers: answersByDepartment,
                  isFocal: requirement.isFocal,
                });
              }

              if (groupRequirements.length > 0) {
                blockRequirements.push({
                  title: block.title,
                  groupRequirements,
                });
              }
            }

            const currentQuestionnaire = findQuestionnaire(response.data.statusQuestionnaires, year, quarter, factory.title, direction.title);

            if (blockRequirements.length > 0) {
              result.push({
                title: gop.title,
                year: String(year),
                quarter,
                factory: factory.code,
                direction: {
                  title: direction.title,
                  blockRequirements,
                },
                completedAssessment: currentQuestionnaire.nameCompleted,
                completionDate: currentQuestionnaire.completionDate,
              });
            }
          }
        }
      }
    }
  }

  return {
    data: result,
    departments: response.params.filters.department.selected.length > 0
                  ? response.data.departments
                    .filter((item) =>
                      response.params.filters.department.selected.includes(item.title),
                    )
                    .sort((a, b) => {
                      if (a.order === null && b.order === null) return 0;
                      if (a.order === null) return 1;
                      if (b.order === null) return -1;
                      return a.order - b.order;
                    })
                  : response.data.departments.sort((a, b) => {
                    if (a.order === null && b.order === null) return 0;
                    if (a.order === null) return 1;
                    if (b.order === null) return -1;
                    return a.order - b.order;
                  })
  };
};
